import { useState } from 'react';
import { Download, FileSpreadsheet } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Button } from '@/components/ui/button';

interface UoMRecord {
  id: string;
  uom: string;
  singular: string;
  plural: string;
  description: string | null;
  is_si: boolean;
  is_imperial: boolean;
  is_active: boolean;
}

interface UoMExportWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  allRecords: UoMRecord[];
  filteredRecords: UoMRecord[];
  hasActiveFilter: boolean;
}

interface ExportColumn {
  key: keyof UoMRecord;
  label: string;
  selected: boolean;
}

const defaultColumns: ExportColumn[] = [
  { key: 'uom', label: 'UoM Code', selected: true },
  { key: 'singular', label: 'Singular', selected: true },
  { key: 'plural', label: 'Plural', selected: true },
  { key: 'description', label: 'Description', selected: true },
  { key: 'is_si', label: 'Is SI', selected: true },
  { key: 'is_imperial', label: 'Is Imperial', selected: true },
  { key: 'is_active', label: 'Is Active', selected: true },
];

export default function UoMExportWizard({
  open,
  onOpenChange,
  allRecords,
  filteredRecords,
  hasActiveFilter,
}: UoMExportWizardProps) {
  const [columns, setColumns] = useState<ExportColumn[]>(defaultColumns);
  const [exportScope, setExportScope] = useState<'all' | 'filtered'>('all');

  const handleColumnToggle = (key: keyof UoMRecord) => {
    setColumns((prev) =>
      prev.map((col) => (col.key === key ? { ...col, selected: !col.selected } : col))
    );
  };

  const handleSelectAll = () => {
    const allSelected = columns.every((col) => col.selected);
    setColumns((prev) => prev.map((col) => ({ ...col, selected: !allSelected })));
  };

  const generateCSV = (data: UoMRecord[], selectedColumns: ExportColumn[]): string => {
    const headers = selectedColumns.map((col) => col.label);
    const rows = data.map((record) =>
      selectedColumns.map((col) => {
        const value = record[col.key];
        if (value === null || value === undefined) return '';
        if (typeof value === 'boolean') return value ? 'true' : 'false';
        const stringValue = String(value);
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      })
    );

    return [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');
  };

  const handleExport = () => {
    const selectedColumns = columns.filter((col) => col.selected);
    if (selectedColumns.length === 0) return;

    const dataToExport = exportScope === 'filtered' ? filteredRecords : allRecords;
    const csv = generateCSV(dataToExport, selectedColumns);

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `uom_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    onOpenChange(false);
  };

  const selectedCount = columns.filter((col) => col.selected).length;
  const recordCount = exportScope === 'filtered' ? filteredRecords.length : allRecords.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" />
            Export Units of Measure
          </DialogTitle>
          <DialogDescription>
            Choose which columns to include and the scope of records to export.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Export Scope */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Records to Export</Label>
            <RadioGroup value={exportScope} onValueChange={(v) => setExportScope(v as 'all' | 'filtered')}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="all" id="all" />
                <Label htmlFor="all" className="font-normal cursor-pointer">
                  All records ({allRecords.length})
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="filtered" id="filtered" disabled={!hasActiveFilter} />
                <Label 
                  htmlFor="filtered" 
                  className={`font-normal cursor-pointer ${!hasActiveFilter ? 'text-muted-foreground' : ''}`}
                >
                  Filtered results only ({filteredRecords.length})
                  {!hasActiveFilter && <span className="text-xs ml-1">(no filter active)</span>}
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Column Selection */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Columns to Include</Label>
              <button
                type="button"
                onClick={handleSelectAll}
                className="text-xs text-primary hover:underline"
              >
                {columns.every((col) => col.selected) ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {columns.map((col) => (
                <div key={col.key} className="flex items-center space-x-2">
                  <Checkbox
                    id={col.key}
                    checked={col.selected}
                    onCheckedChange={() => handleColumnToggle(col.key)}
                  />
                  <Label htmlFor={col.key} className="text-sm font-normal cursor-pointer">
                    {col.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <p className="text-xs text-muted-foreground mr-auto">
            {selectedCount} columns, {recordCount} records
          </p>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            disabled={selectedCount === 0 || recordCount === 0}
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
